import { useState } from "react";
import { bool, func, number, object, oneOf, oneOfType, string, shape } from "prop-types";
import { useStores } from "@hooks";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { CircularProgress, Grid } from "@material-ui/core";
import { Button } from "@components";
import { pricing } from "@paths";
import { formatPrice } from "@utils";
import { CouponDialog } from "../CouponDialog";
import useStyle from "./SubscriptionSections.style";
import classnames from "classnames";

export const SectionPlan = observer(({
  name,
  packageName,
  isLoading,
  hasSubscription,
  currency,
  duration,
  estimates,
  users,
  price,
  discountPrice,
  coupon,
  setCoupon,
}) => {

  const { t } = useTranslation();
  const classes = useStyle();
  const { userStore } = useStores();
  const isFreePrice = Number(price) === 0 || price === undefined || packageName === "package_free" || packageName === "package_trial";
  const [couponDialogOpened, openCouponDialog] = useState(false);

  return (
    <Grid
      item container
      xs={12} md={4}
      className={`${classes.box} ${classes.bordered}`}
    >
      <p className="font-bold text-xl">
        {t("views.subscription.titles.current")}
      </p>
      {
        isLoading &&
        <CircularProgress
          aria-label="progress indicator"
          size={40}
          color="primary"
          className={classes.spinner}
        />
      }
      {
        !isLoading &&
        <Grid item container direction="column">
          <p className="color-primary text-3xl">{name}</p>
          {
            !userStore.hasTrialLeft &&
            <p className="transparent-2">
              {
                (!hasSubscription || !duration)
                ? t("billing.forever")
                : t(`billing.per_${duration}_fancy`)
              }
            </p>
          }
          <p className="transparent-2">
            {
              hasSubscription &&
              `${users} ${t("common.users")}, ${estimates} ${t(
                "common.estimates"
              )}`
            }
          </p>
          <p className="mt-2">
            <span className={classnames("font-bold relative", discountPrice ? "text-xl" : "text-2xl")}>
              {isFreePrice ? t("plans.package_free") : formatPrice(price || 0, currency, 0)}
              {
                (typeof discountPrice === "number") &&
                <div className={classes.discountLine} />
              }
            </span>
            {
              (typeof discountPrice === "number") &&
              <span className="font-bold text-2xl ml-1">
                {formatPrice(discountPrice || 0, currency, 0)}
              </span>
            }
            {
              Boolean(duration) && !isFreePrice &&
              <span className="font-bold text-lg">
                {" / "}
                {t(`time.${duration}`)}
              </span>
            }
          </p>
        </Grid>
      }
      <Grid item container justifyContent="space-between" spacing={1}>
        <Grid item>
          <Button
            variant="outlined"
            size="small"
            to={pricing}
            disabled={isLoading}
          >
            {t("views.subscription.actions.change_plan")}
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant="outlined"
            size="small"
            onClick={() => openCouponDialog(true)}
            disabled={isLoading}
          >
            {t("views.subscription.actions.add_coupon")}
          </Button>
        </Grid>
      </Grid>
      <CouponDialog
        open={couponDialogOpened}
        onClose={() => openCouponDialog(false)}
        coupon={coupon}
        setCoupon={(c) => setCoupon(c)}
      />
    </Grid>
  );
});

SectionPlan.propTypes = {
  name: string.isRequired,
  packageName: string,
  isLoading: bool,
  hasSubscription: bool,
  duration: oneOf(["month", "year"]),
  currency: shape({
    code: string.isRequired,
    symbolStart: string,
    symbolEnd: string,
  }).isRequired,
  estimates: number,
  users: number,
  price: number,
  discountPrice: number,
  coupon: oneOfType([string, object]),
  setCoupon: func,
};
